import React from 'react';

import { Badge } from '@ovpn-ui/core';
import { colors } from '@ovpn-ui/styles';
import {
    ApiApp,
    Users,
    World,
    Server,
    Category,
    Shield,
    Settings,
    News,
    ChartLine,
    Apps,
    Devices,
    Help,
    Download,
} from '@ovpn-ui/icons';

import { getEnvVar } from '../utils';

import { routes } from './routes';

function activateOnExcept(on: string, except: string[]) {
    const negativeLookahead = `(?!${except.join('|')})`;
    return new RegExp(`${negativeLookahead}(${on}/.*)`);
}

const makeBadge = (text: 'Beta' | 'New') => {
    const props =
        text === 'Beta'
            ? { color: colors.neutralTextTitle, backgroundColor: colors.secondaryBase }
            : { color: colors.neutralWhite, backgroundColor: colors.successBase };

    return (
        <Badge size="xs" {...props}>
            {text}
        </Badge>
    );
};

export const menuLinks = {
    status: {
        name: 'Status',
        to: routes.statusPage,
        icon: <ChartLine />,
    },
    users: {
        name: 'Users',
        icon: <Users />,
        to: routes.users,
        children: {
            users: {
                name: 'Users',
                to: routes.users,
                activeOn: [routes.userDetails, routes.userDetailsDevices],
            },
            groups: {
                name: 'Groups',
                to: routes.groups,
                activeOn: [
                    routes.groupDetails,
                    routes.groupDetailsUsers,
                    routes.groupDetailsAccess,
                ],
            },
            tenantDevices: {
                name: 'Devices',
                to: routes.devices,
            },
            locationContext: {
                name: 'Location Context',
                to: routes.locationContext.all,
                activeOn: Object.values(routes.locationContext),
            },
            devicePosture: {
                name: 'Device Posture',
                to: routes.devicePosture.all,
                activeOn: Object.values(routes.devicePosture),
                decorator: makeBadge('Beta'),
            },
        },
    },
    network: {
        name: 'Networks',
        to: routes.networks,
        icon: <World />,
        children: {
            networks: {
                name: 'Networks',
                to: routes.networks,
                activeOn: activateOnExcept(routes.networks, [
                    routes.networkApplications,
                    routes.networkConnectors,
                    routes.networkIPServices,
                ]),
            },
            applications: {
                name: 'Applications',
                to: routes.networkApplications,
            },
            ipServices: {
                name: 'IP Services',
                to: routes.networkIPServices,
            },
            connectors: {
                name: 'Connectors',
                to: routes.networkConnectors,
            },
        },
    },
    hosts: {
        name: 'Hosts',
        to: routes.hosts,
        icon: <Server />,
        children: {
            hosts: {
                name: 'Hosts',
                to: routes.hosts,
                activeOn: activateOnExcept(routes.hosts, [
                    routes.hostApplications,
                    routes.hostConnectors,
                    routes.hostIPServices,
                ]),
            },
            applications: {
                name: 'Applications',
                to: routes.hostApplications,
            },
            ipServices: {
                name: 'IP Services',
                to: routes.hostIPServices,
            },
            connectors: {
                name: 'Connectors',
                to: routes.hostConnectors,
            },
        },
    },
    access: {
        name: 'Access',
        icon: <Category />,
        to: routes.accessControl,
        children: {
            visibility: {
                name: 'Visibility',
                to: routes.accessVisibility,
                activeOn: new RegExp(`${routes.accessVisibility}/.*`),
            },
            groups: {
                name: 'Groups',
                to: routes.accessControl,
                activeOn: [
                    routes.accessGroupCreate,
                    routes.accessGroupEdit,
                    routes.accessGroupEditV2,
                ],
            },
            internet: {
                name: 'Internet',
                to: routes.accessInternet.groups,
                activeOn: [routes.accessInternet.networks, routes.accessInternet.hosts],
            },
        },
    },
    shieldV2: {
        name: 'Shield',
        icon: <Shield />,
        to: routes.cyberShieldV2.overview,
        children: {
            overview: {
                name: 'Overview',
                to: routes.cyberShieldV2.overview,
            },
            configuration: {
                name: 'Configuration',
                to: routes.cyberShieldV2.configuration,
                activeOn: new RegExp(`${routes.cyberShieldV2.configuration}/.*`),
            },
            metrics: {
                name: 'Metrics',
                to: routes.cyberShieldV2.metrics,
            },
        },
    },

    publishing: {
        name: 'AppHub',
        to: routes.publishing,
        icon: <Apps />,
        activeOn: new RegExp(`${routes.publishing}/.*`),
    },
    apiAndLogs: {
        name: 'API & Logs',
        icon: <ApiApp />,
        to: routes.ssoApi,
        children: {
            cloudApi: {
                name: 'API',
                activeOn: new RegExp(`${routes.ssoApi}/.*`),
                to: routes.ssoApi,
            },
            auditLogs: {
                name: 'Audit Log',
                decorator: makeBadge('Beta'),
                activeOn: new RegExp(`${routes.auditLog}/.*`),
                to: routes.auditLog,
            },
            dnsResolutions: {
                name: 'DNS Log',
                decorator: makeBadge('Beta'),
                activeOn: new RegExp(`${routes.dnsResolutions}/.*`),
                to: routes.dnsResolutions,
            },
            logStreaming: {
                name: 'Log Streaming',
                activeOn: new RegExp(`${routes.logStreaming}/.*`),
                to: routes.logStreaming,
            },
        },
    },
    settings: {
        name: 'Settings',
        icon: <Settings />,
        to: routes.networkSettings,
        children: {
            vpn: {
                name: 'WPC',
                to: routes.networkSettings,
                title: 'WPC - Wide-Area Private Cloud',
            },
            users: {
                name: 'Users',
                to: routes.userSettings,
            },
            userAuth: {
                name: 'User Authentication',
                to: routes.userAuth,
                activeOn: [
                    routes.samlGroupMapping,
                    ...Object.values(routes.ldapSettings),
                    ...Object.values(routes.ssoSamlConfigure),
                ],
            },
            dns: {
                name: 'DNS',
                to: routes.dnsServers,
                activeOn: [routes.dnsRecords],
            },
            notifications: {
                name: 'Notifications',
                to: routes.notificationPreferences,
            },
        },
    },
    documentation: {
        group: 'docs',
        name: 'Help',
        icon: <News />,
        to: routes.quickStart,
        children: {
            wizard: {
                name: 'Restart onboarding',
                to: routes.wizard.restart,
            },
            quickStart: {
                name: 'Quick Start',
                to: routes.quickStart,
            },
            knowledgeBase: {
                name: 'Knowledge Base',
                href: getEnvVar('DOCUMENTATION_LINK'),
                target: '_blank',
            },
            support: {
                name: 'Support',
                href: 'mailto:cloud-support@openvpn.net',
            },
        },
    },
    getConnected: {
        group: 'download',
        name: 'Download App',
        icon: <Download />,
        to: routes.getConnectedUnified,
    },
    instructions: {
        name: 'Get Connected',
        to: routes.instructions,
        icon: <Help />,
    },
    devices: {
        name: 'My Devices',
        to: routes.devices,
        icon: <Devices />,
    },
} as const;
